import { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * One-time modal to pick a public username.
 * Enforced by backend: 3-24 chars, starts with a letter, profanity-blocked,
 * globally unique (case-insensitive).
 */
export default function UsernameSetupModal({ onClose, onSuccess }) {
  const [value, setValue] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/wanted-list/username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username: value.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data.detail || 'Something went wrong.');
      } else {
        onSuccess?.(data.username);
      }
    } catch (e2) {
      setErr('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      data-testid="username-setup-modal"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-[#0C0C0C] border border-zinc-800 rounded-2xl shadow-2xl p-6"
      >
        <h3 className="text-xl font-extrabold text-white mb-1">Pick a public username</h3>
        <p className="text-sm text-zinc-400 mb-4">
          This is what other sellers & wanters see when you match. No spaces — letters, numbers,
          “_”, “.” and “-” only.
        </p>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. charity_charlie"
          autoFocus
          maxLength={24}
          className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white font-mono focus:outline-none focus:border-[#00D26A]"
          data-testid="username-input"
        />
        {err && (
          <p className="text-sm mt-2" style={{ color: '#E04A3F' }} data-testid="username-error">
            {err}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full font-bold text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="username-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || value.trim().length < 3}
            className="px-5 py-2 rounded-full font-bold text-sm disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
            data-testid="username-submit-btn"
          >
            {loading ? 'Saving…' : 'Save username'}
          </button>
        </div>
      </form>
    </div>
  );
}
